// 애플리케이션 기본 설정
export const APP_CONFIG = {
  NAME: import.meta.env.VITE_APP_NAME || 'DRiveFront',
  VERSION: import.meta.env.VITE_APP_VERSION || '1.1.5',
  DESCRIPTION: import.meta.env.VITE_APP_DESCRIPTION || 'DRive Frontend Application',
};

// API 설정
export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_BASE_URL || '', // vite 프록시 사용을 위해 빈 문자열로 설정
  TIMEOUT: parseInt(import.meta.env.VITE_API_TIMEOUT) || 10000,
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
};

// 개발 환경 설정
export const DEV_CONFIG = {
  IS_DEV: import.meta.env.DEV,
  IS_PROD: import.meta.env.PROD,
  DEBUG_MODE: import.meta.env.VITE_DEBUG_MODE === 'true',
  ENABLE_LOGGING: import.meta.env.VITE_ENABLE_LOGGING === 'true',
  ENABLE_ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
};

// 라우팅 설정
export const ROUTES = {
  HOME: '/',
  NOT_FOUND: '/404',
  FORBIDDEN: '/403',
  LOGIN: '/login',
  SYSTEM_LOGIN: '/system-login',
  REGISTER: '/register',
  DASHBOARD: '/dashboard',
  MAP_DASHBOARD: '/map-dashboard',
  CHARGER_MANAGEMENT: '/charger-management',
  STATION_MANAGEMENT: '/station-management',
  DR_MANAGEMENT: '/dr-management',
  DR_HISTORY: '/dr-history',
  DR_HISTORY_DETAIL: '/dr-history/detail',
  REPORT_MANAGEMENT: '/report-request-management',
  SETTINGS: '/settings',
  MESSAGE_MANAGEMENT: '/message-management',
  SYSTEM_LOG: '/system-log',
  ROLE_MANAGEMENT: '/role-management',
  USER_MANAGEMENT: '/user-management',
  AI_MODEL_MANAGEMENT: '/ai-model-management',
  MODULE_MANAGEMENT: '/ai-module-management',
  CBL_MODULE_MANAGEMENT: '/cbl-module-management',
  SYSTEM_ALERT_HISTORY: '/system-alert-history',
  SYSTEM_ALERT_DETAIL: '/system-alert-history/detail',
  SYSTEM_RECOVERY_COMMAND: '/system-recovery-command',
  SYSTEM_CONFIG_KEY_MANAGEMENT: '/settings/system-config-key-management',
  CHANGE_PASSWORD: '/change-password',
  SMS_MANAGEMENT: '/sms-management',
  OPM_DATA_LINKAGE_STATUS: '/opm-data-linkage-status',
};

// 메뉴와 페이지의 1대1 매핑
export const MENU_PAGE_MAPPING = {
  'home': ROUTES.HOME,
  'dashboard': ROUTES.DASHBOARD,
  'map-dashboard': ROUTES.MAP_DASHBOARD,
  'charger-management': ROUTES.CHARGER_MANAGEMENT,
  'station-management': ROUTES.STATION_MANAGEMENT,
  'dr-management': ROUTES.DR_MANAGEMENT,
  'dr-history': ROUTES.DR_HISTORY,
  'report-request-management': ROUTES.REPORT_MANAGEMENT,
  'settings': ROUTES.SETTINGS,
  'system-log': ROUTES.SYSTEM_LOG,
  'user-management': ROUTES.USER_MANAGEMENT,
  'ai-module-management': ROUTES.MODULE_MANAGEMENT,
  'cbl-module-management': ROUTES.CBL_MODULE_MANAGEMENT,
  'system-alert-history': ROUTES.SYSTEM_ALERT_HISTORY,
  'system-recovery-command': ROUTES.SYSTEM_RECOVERY_COMMAND,
  'sms-management': ROUTES.SMS_MANAGEMENT,
  'opm-data-linkage-status': ROUTES.OPM_DATA_LINKAGE_STATUS,
};

// 메뉴 식별자와 표시명 매핑
export const MENU_LABELS = {
  'home': '홈',
  'dashboard': '대시보드',
  'map-dashboard': '지도 대시보드',
  'charger-management': '충전기 관리',
  'station-management': '충전소 관리',
  'dr-management': 'DR 자원 관리',
  'dr-history': 'DR 이력 및 정산',
  'report-request-management': '보고서 관리',
  'settings': '시스템 설정',
  'message-management': '메시지 관리',
  'system-log': '시스템 로그',
  'role-management': '역할 관리',
  'user-management': '사용자 및 역할 관리',
  'ai-module-management': 'AI 모듈 관리',
  'cbl-module-management': 'CBL 모듈관리',
  'system-alert-history': '시스템 알림 내역',
  'system-recovery-command': '시스템 복구 명령',
  'sms-management': 'SMS 발송 관리 및 이력',
  'opm-data-linkage-status': '고객계약번호 상태 조회',
};

// 로컬 스토리지 키 (prefix를 추가하여 충돌 방지)
const STORAGE_PREFIX = 'drivefront:';

export const STORAGE_KEYS = {
  AUTH_TOKEN: `${STORAGE_PREFIX}auth_token`,
  ACCESS_TOKEN: `${STORAGE_PREFIX}access_token`,
  REFRESH_TOKEN: `${STORAGE_PREFIX}refresh_token`,
  USER_DATA: `${STORAGE_PREFIX}user_data`,
  THEME: `${STORAGE_PREFIX}theme`,
  LANGUAGE: `${STORAGE_PREFIX}language`,
  SIDEBAR_COLLAPSED: `${STORAGE_PREFIX}sidebar_collapsed`,
  ROLES: `${STORAGE_PREFIX}roles`,
  ROLE_PERMISSIONS: `${STORAGE_PREFIX}role_permissions`,
  USER_ROLES: `${STORAGE_PREFIX}user_roles`, // 사용자의 역할 배열
  USER_FEATURES: `${STORAGE_PREFIX}user_features`, // 사용자의 피처 권한 배열
  DASHBOARD_SELECTED_REGION: `${STORAGE_PREFIX}dashboard_selected_region`,
  WEATHER_CARD_SELECTED_REGION: `${STORAGE_PREFIX}weather_card_selected_region`,
};

// 테마 설정
export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
};

// 언어 설정
export const LANGUAGES = {
  KO: 'ko',
  EN: 'en',
  JA: 'ja',
  ZH: 'zh',
};

// 페이지네이션 설정
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [5, 10, 20, 50, 100],
  MAX_PAGE_SIZE: 100,
};

// 에러 메시지
export const ERROR_MESSAGES = {
  NETWORK_ERROR: '네트워크 오류가 발생했습니다.',
  UNAUTHORIZED: '인증이 필요합니다.',
  FORBIDDEN: '접근 권한이 없습니다.',
  NOT_FOUND: '요청한 리소스를 찾을 수 없습니다.',
  SERVER_ERROR: '서버 오류가 발생했습니다.',
  VALIDATION_ERROR: '입력값을 확인해주세요.',
  UNKNOWN_ERROR: '알 수 없는 오류가 발생했습니다.',
};

// 성공 메시지
export const SUCCESS_MESSAGES = {
  SAVE_SUCCESS: '저장되었습니다.',
  DELETE_SUCCESS: '삭제되었습니다.',
  UPLOAD_SUCCESS: '업로드되었습니다.',
  LOGIN_SUCCESS: '로그인되었습니다.',
  LOGOUT_SUCCESS: '로그아웃되었습니다.',
  REGISTER_SUCCESS: '회원가입이 완료되었습니다.',
};

// 기본 사용자 역할 정의 (역할 관리 시스템에서 동적으로 관리됨)
export const USER_ROLES = {
  SYSTEM_ADMIN: 'SystemAdmin',
  ADMIN: 'Admin',
  USER: 'User',
  GUEST: 'Guest',
};

// 시스템 피처 식별자 (서버에서 관리하는 피처 이름과 매칭)
export const FEATURES = {
  DASHBOARD: 'DASHBOARD',
  MAP_DASHBOARD: 'MAP_DASHBOARD',
  CHARGER_MANAGEMENT: 'CHARGER_MANAGEMENT',
  STATION_MANAGEMENT: 'STATION_MANAGEMENT',
  DR_MANAGEMENT: 'DR_MANAGEMENT',
  REPORT_MANAGEMENT: 'REPORT_MANAGEMENT',
  AI_MODULE_MANAGEMENT: 'AI_MODULE_MANAGEMENT',
  CBL_MODULE_MANAGEMENT: 'CBL_MODULE_MANAGEMENT',
  USER_ROLE_MANAGEMENT: 'USER_ROLE_MANAGEMENT',
  DR_HISTORY: 'DR_HISTORY',
  SYSTEM_ALERT_HISTORY: 'SYSTEM_ALERT_HISTORY',
  SYSTEM_SETTING: 'SYSTEM_SETTING',
  SYSTEM_LOG: 'SYSTEM_LOG',
  SYSTEM_RECOVERY_COMMAND: 'SYSTEM_RECOVERY_COMMAND',
  SMS_MANAGEMENT: 'SMS_MANAGEMENT',
  OPM_DATA_LINKAGE_STATUS: 'OPM_DATA_LINKAGE_STATUS',
};

// 피처 식별자와 한글 이름 매핑
export const FEATURE_NAMES = {
  [FEATURES.DASHBOARD]: '대시보드',
  [FEATURES.MAP_DASHBOARD]: '지도대시보드',
  [FEATURES.CHARGER_MANAGEMENT]: '충전기관리',
  [FEATURES.STATION_MANAGEMENT]: '충전소관리',
  [FEATURES.DR_MANAGEMENT]: 'DR자원관리',
  [FEATURES.REPORT_MANAGEMENT]: '보고서관리',
  [FEATURES.AI_MODULE_MANAGEMENT]: 'AI모듈관리',
  [FEATURES.CBL_MODULE_MANAGEMENT]: 'CBL모듈관리',
  [FEATURES.USER_ROLE_MANAGEMENT]: '사용자및역할관리',
  [FEATURES.DR_HISTORY]: 'DR이력및정산',
  [FEATURES.SYSTEM_ALERT_HISTORY]: '시스템알림내역',
  [FEATURES.SYSTEM_SETTING]: '시스템설정',
  [FEATURES.SYSTEM_LOG]: '시스템로그',
  [FEATURES.SYSTEM_RECOVERY_COMMAND]: '시스템복구명령',
  [FEATURES.SMS_MANAGEMENT]: 'SMS발송관리',
  [FEATURES.OPM_DATA_LINKAGE_STATUS]: 'OPM데이터연동상태관리',
};

// 피처 식별자와 메뉴 식별자 매핑
export const FEATURE_TO_MENU_MAPPING = {
  [FEATURES.DASHBOARD]: 'dashboard',
  [FEATURES.MAP_DASHBOARD]: 'map-dashboard',
  [FEATURES.CHARGER_MANAGEMENT]: 'charger-management',
  [FEATURES.STATION_MANAGEMENT]: 'station-management',
  [FEATURES.DR_MANAGEMENT]: 'dr-management',
  [FEATURES.REPORT_MANAGEMENT]: 'report-request-management',
  [FEATURES.AI_MODULE_MANAGEMENT]: 'ai-module-management', // AI 모듈 관리
  [FEATURES.CBL_MODULE_MANAGEMENT]: 'cbl-module-management', // CBL모듈관리
  [FEATURES.USER_ROLE_MANAGEMENT]: 'user-management', // 사용자및역할관리 -> 사용자관리로 매핑
  [FEATURES.DR_HISTORY]: 'dr-history',
  [FEATURES.SYSTEM_ALERT_HISTORY]: 'system-alert-history',
  [FEATURES.SYSTEM_SETTING]: 'settings',
  [FEATURES.SYSTEM_LOG]: 'system-log',
  [FEATURES.SYSTEM_RECOVERY_COMMAND]: 'system-recovery-command', // 시스템복구명령
  [FEATURES.SMS_MANAGEMENT]: 'sms-management', // SMS발송관리
  [FEATURES.OPM_DATA_LINKAGE_STATUS]: 'opm-data-linkage-status',
};

// 페이지 경로를 직접 피처로 매핑 (메뉴가 없는 상세 페이지 등)
export const PAGE_TO_FEATURE_MAPPING = {
  // [ROUTES.SYSTEM_CONFIG_KEY_MANAGEMENT]: FEATURES.SYSTEM_CONFIG_KEY_MANAGEMENT,
}; 